export const privacyPolicy = {
    title: "Privacy Policy",
    lastUpdated: "January 15, 2025",
    effectiveDate: "January 15, 2025",
    
    introduction: `This Privacy Policy describes how Inked Up Manager ("we," "us," or "our") collects, uses, shares, and protects information when you use our software platform and related services (the "Service").`,
    
    importantNotice: `This policy applies to: Tattoo artists and shops who subscribe to our Service ("Users"), Clients who interact with our platform through their chosen artist/shop ("Clients"), and Website visitors and prospective customers.`,
    
    sections: [
      {
        id: "information-we-collect",
        title: "INFORMATION WE COLLECT",
        subsections: [
          {
            id: "from-artists-shops",
            title: "Information from Tattoo Artists/Shops",
            content: [
              "Account and Business Information:",
              "• Name, email address, phone number",
              "• Business name, address, and tax identification",
              "• Professional license numbers and certifications", 
              "• Payment and billing information",
              "• Business contact preferences",
              "",
              "Platform Usage Data:",
              "• Login times and frequency",
              "• Features accessed and usage patterns",
              "• Device information (IP address, browser type, operating system)",
              "• Performance and error logs",
              "",
              "Content You Create:",
              "• Custom consent forms and templates",
              "• Client communications and notes",
              "• Business settings and preferences",
              "• Uploaded images and documents"
            ]
          },
          {
            id: "from-clients",
            title: "Information from Clients",
            content: [
              "Personal Information:",
              "• Name, email address, phone number, date of birth",
              "• Mailing address and emergency contact information",
              "• Health information relevant to tattoo services (allergies, medical conditions)",
              "• Consent form responses and digital signatures",
              "",
              "Service-Related Information:",
              "• Appointment history and preferences",
              "• Service types and descriptions",
              "• Photos and documentation of body art (when provided)",
              "• Payment information for completed transactions",
              "",
              "Technical Information:",
              "• Device and browser information",
              "• IP address and location data",
              "• Platform interaction logs"
            ]
          },
          {
            id: "from-all-users",
            title: "Information from All Users",
            content: "Communications:",
            list: [
              "Support ticket correspondence",
              "Email communications",
              "Feedback and survey responses",
              "Marketing interaction data"
            ]
          }
        ]
      },
  
      {
        id: "how-we-use-info",
        title: "HOW WE USE YOUR INFORMATION",
        subsections: [
          {
            id: "primary-service-functions",
            title: "Primary Service Functions",
            content: [
              "For Tattoo Artists/Shops:",
              "• Provide platform access and core features",
              "• Process payments and billing",
              "• Enable client management and appointment scheduling",
              "• Facilitate consent form creation and digital signatures",
              "• Provide customer support and technical assistance",
              "",
              "For Clients:",
              "• Process consent forms and appointments",
              "• Enable communication with your chosen artist/shop",
              "• Facilitate payment processing",
              "• Provide confirmation and reminder services"
            ]
          },
          {
            id: "business-operations",
            title: "Business Operations",
            content: [
              "Service Improvement:",
              "• Analyze usage patterns to enhance platform features",
              "• Identify and fix technical issues",
              "• Develop new tools and capabilities",
              "• Conduct quality assurance testing",
              "",
              "Legal and Compliance:",
              "• Comply with applicable laws and regulations",
              "• Respond to legal requests and court orders",
              "• Prevent fraud and abuse",
              "• Enforce our Terms of Service",
              "",
              "Communications:",
              "• Send service-related notifications",
              "• Provide customer support",
              "• Share important updates and security alerts",
              "• Send marketing communications (with consent)"
            ]
          }
        ]
      },
  
      {
        id: "information-sharing",
        title: "INFORMATION SHARING AND DISCLOSURE",
        subsections: [
          {
            id: "with-artists-shops",
            title: "With Tattoo Artists/Shops",
            content: "Client Data Sharing: When you complete forms or book appointments, your information is shared with your chosen artist/shop including:",
            list: [
              "Personal contact information",
              "Health and medical information you provide",
              "Consent form responses and signatures",
              "Appointment and service history",
              "Payment confirmation details"
            ]
          },
          {
            id: "service-providers",
            title: "Service Providers",
            content: [
              "Third-Party Processors: We share information with trusted service providers who assist in operating our platform:",
              "• Payment Processors (Stripe): Credit card and billing information",
              "• Email Services (SendGrid): Communication delivery",
              "• Cloud Hosting (AWS/Digital Ocean): Secure data storage",
              "• Analytics Providers: Usage statistics (anonymized when possible)",
              "• Customer Support Tools: Support ticket management",
              "",
              "Data Processing Agreements: All service providers must sign data processing agreements ensuring they:",
              "• Use data only for specified purposes",
              "• Implement appropriate security measures",
              "• Delete or return data upon contract termination",
              "• Comply with applicable privacy laws"
            ]
          },
          {
            id: "legal-requirements",
            title: "Legal Requirements",
            content: "Mandatory Disclosures: We may disclose information when required by:",
            list: [
              "Valid court orders or subpoenas",
              "Government investigations",
              "Law enforcement requests with proper authority",
              "Emergency situations involving imminent harm"
            ]
          },
          {
            id: "business-transfers",
            title: "Business Transfers",
            content: "In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity with appropriate notice and protection measures."
          },
          {
            id: "with-consent",
            title: "With Your Consent",
            content: "We may share information for purposes not described in this policy with your explicit consent."
          }
        ]
      },
  
      {
        id: "data-retention",
        title: "DATA RETENTION",
        subsections: [
          {
            id: "active-account-data",
            title: "Active Account Data",
            content: [
              "During Subscription:",
              "• User account data: Retained while subscription is active",
              "• Client data: Retained per user's business needs and legal requirements",
              "• Platform usage data: Retained for service operation and improvement"
            ]
          },
          {
            id: "post-cancellation",
            title: "Post-Cancellation",
            content: [
              "Account Termination:",
              "• Users have 30 days to download their data after cancellation",
              "• Most data is deleted within 90 days of account closure",
              "• Some data may be retained longer for legal compliance",
              "",
              "Legal Retention Requirements:",
              "• Financial records: 7 years (tax compliance)",
              "• Consent forms: Per state health department requirements (typically 7-10 years)",
              "• Dispute-related records: Until resolution plus statute of limitations"
            ]
          },
          {
            id: "client-data-deletion",
            title: "Client Data Deletion",
            content: "Clients may request deletion of their personal information, subject to:",
            list: [
              "Tattoo shop's legal record-keeping requirements",
              "Ongoing business relationships",
              "Legal holds or disputes"
            ]
          }
        ]
      },
  
      {
        id: "data-security",
        title: "DATA SECURITY",
        subsections: [
          {
            id: "technical-safeguards",
            title: "Technical Safeguards",
            content: [
              "Encryption:",
              "• All data transmitted using TLS 1.3 encryption",
              "• Database encryption at rest using AES-256",
              "• Encrypted backups with separate key management",
              "",
              "Access Controls:",
              "• Multi-factor authentication for administrative access",
              "• Role-based access permissions",
              "• Regular access audits and reviews",
              "",
              "Infrastructure Security:",
              "• SOC 2 compliant hosting providers",
              "• Regular security assessments and penetration testing",
              "• Automated security monitoring and alerting",
              "• Secure development practices and code reviews"
            ]
          },
          {
            id: "operational-safeguards",
            title: "Operational Safeguards",
            content: [
              "Employee Training:",
              "• Privacy and security training for all staff",
              "• Background checks for personnel with data access",
              "• Confidentiality agreements and data handling policies",
              "",
              "Incident Response:",
              "• 24/7 security monitoring",
              "• Documented incident response procedures",
              "• Breach notification protocols (within 72 hours)",
              "• Regular backup testing and disaster recovery drills"
            ]
          },
          {
            id: "user-security-responsibilities",
            title: "User Security Responsibilities",
            content: "Account Protection:",
            list: [
              "Use strong, unique passwords",
              "Enable two-factor authentication",
              "Keep software and devices updated",
              "Report suspicious activity immediately"
            ]
          }
        ]
      },
  
      {
        id: "privacy-rights",
        title: "YOUR PRIVACY RIGHTS",
        subsections: [
          {
            id: "access-portability",
            title: "Access and Portability",
            content: "Data Access:",
            list: [
              "Request copies of your personal information",
              "Download your data in commonly used formats",
              "Receive information about how your data is processed"
            ]
          },
          {
            id: "correction-updates",
            title: "Correction and Updates",
            content: "Data Accuracy:",
            list: [
              "Correct inaccurate personal information",
              "Update outdated contact or business details",
              "Modify privacy preferences and communication settings"
            ]
          },
          {
            id: "deletion-rights",
            title: "Deletion Rights",
            content: "Right to Deletion:",
            list: [
              "Request deletion of personal information (subject to legal requirements)",
              "Close your account and associated data",
              "Opt out of non-essential data processing"
            ]
          },
          {
            id: "communication-preferences",
            title: "Communication Preferences",
            content: "Marketing Controls:",
            list: [
              "Unsubscribe from marketing emails",
              "Opt out of promotional communications",
              "Adjust notification preferences"
            ]
          },
          {
            id: "exercising-rights",
            title: "Exercising Your Rights",
            content: [
              "How to Submit Requests:",
              "• Email: privacy@inkedupmanager.com",
              "• Account settings dashboard",
              "• Written request to our business address",
              "",
              "Response Timeline:",
              "• Acknowledgment within 5 business days",
              "• Complete response within 30 days",
              "• Complex requests may require additional time with notice"
            ]
          }
        ]
      },
  
      {
        id: "cookies-tracking",
        title: "COOKIES AND TRACKING",
        subsections: [
          {
            id: "types-cookies",
            title: "Types of Cookies",
            content: [
              "Essential Cookies:",
              "• Authentication and session management",
              "• Security and fraud prevention",
              "• Core platform functionality",
              "",
              "Analytics Cookies:",
              "• Usage statistics and performance monitoring",
              "• Feature adoption and user experience analysis",
              "• Platform optimization and improvement",
              "",
              "Preference Cookies:",
              "• User interface customization",
              "• Language and accessibility settings",
              "• Communication preferences"
            ]
          },
          {
            id: "cookie-management",
            title: "Cookie Management",
            content: [
              "Browser Controls: Most browsers allow you to:",
              "• Block or delete cookies",
              "• Set cookie preferences",
              "• Receive cookie notifications",
              "",
              "Platform Controls:",
              "• Adjust tracking preferences in account settings",
              "• Opt out of non-essential analytics",
              "• Review and modify cookie consent"
            ]
          }
        ]
      },
  
      {
        id: "international-transfers",
        title: "INTERNATIONAL DATA TRANSFERS",
        subsections: [
          {
            id: "data-location",
            title: "Data Location",
            content: [
              "Primary Storage: Our servers are located in the United States with backup facilities in secure data centers.",
              "",
              "Cross-Border Transfers: If you are located outside the United States, your information may be transferred to and processed in the United States."
            ]
          },
          {
            id: "transfer-safeguards",
            title: "Transfer Safeguards",
            content: "Protection Measures:",
            list: [
              "Standard contractual clauses for international transfers",
              "Adequacy decisions where applicable",
              "Additional safeguards for sensitive data"
            ]
          }
        ]
      },
  
      {
        id: "childrens-privacy",
        title: "CHILDREN'S PRIVACY",
        subsections: [
          {
            id: "age-restrictions",
            title: "Age Restrictions",
            content: "No Collection from Children:",
            list: [
              "We do not knowingly collect information from children under 13",
              "Users must be 18+ or have parental consent for tattoo services",
              "Parents should supervise any platform interactions by minors"
            ]
          },
          {
            id: "parental-controls",
            title: "Parental Controls",
            content: "If We Learn of Child Data:",
            list: [
              "Immediate deletion of collected information",
              "Account suspension if necessary",
              "Notification to parents/guardians"
            ]
          }
        ]
      },
  
      {
        id: "state-privacy-rights",
        title: "STATE-SPECIFIC PRIVACY RIGHTS",
        subsections: [
          {
            id: "california-residents",
            title: "California Residents (CCPA/CPRA)",
            content: [
              "Additional Rights:",
              "• Right to know what personal information is collected",
              "• Right to delete personal information",
              "• Right to opt out of sale of personal information",
              "• Right to non-discrimination for exercising privacy rights",
              "",
              "California Disclosures:",
              "• We do not sell personal information to third parties",
              "• We may share information for business purposes as described above",
              "• You may request specific details about data sharing in the past 12 months"
            ]
          },
          {
            id: "other-state-laws",
            title: "Other State Laws",
            content: "We comply with applicable state privacy laws including those in Virginia, Colorado, Connecticut, and other states with comprehensive privacy legislation."
          }
        ]
      },
  
      {
        id: "healthcare-information",
        title: "HEALTHCARE INFORMATION",
        subsections: [
          {
            id: "hipaa-considerations",
            title: "HIPAA Considerations",
            content: [
              "Limited Healthcare Data: While we process some health-related information, we are generally not a \"covered entity\" under HIPAA. However:",
              "• We implement HIPAA-level security measures",
              "• We limit health data processing to necessary purposes",
              "• We provide business associate agreements when required"
            ]
          },
          {
            id: "sensitive-information",
            title: "Sensitive Information",
            content: "Special Protections:",
            list: [
              "Health information is encrypted and access-controlled",
              "Shared only with authorized tattoo professionals",
              "Retained according to healthcare record requirements"
            ]
          }
        ]
      },
  
      {
        id: "contact-information",
        title: "CONTACT INFORMATION",
        subsections: [
          {
            id: "privacy-officer",
            title: "Privacy Officer",
            content: [
              "For Privacy Questions:",
              "Email: privacy@inkedupmanager.com",
              //"Phone: [Privacy Phone Number]",
              "Address: 1250 Scenic Highway S STE 1701 #206, Lawrenceville, GA 30045"
            ]
          },
          {
            id: "data-protection-officer",
            title: "Data Protection Officer",
            content: [
              "For EU/UK Residents:",
              "Email: dpo@inkedupmanager.com",
              "Address: [DPO Address if applicable]"
            ]
          },
          {
            id: "general-contact",
            title: "General Contact",
            content: [
              "Customer Support:",
              "Email: support@inkedupmanager.com",
              //"Phone: [Support Phone Number]",
              "Business Hours: Monday-Friday, 9 AM - 5 PM EST"
            ]
          }
        ]
      },
  
      {
        id: "changes-policy",
        title: "CHANGES TO THIS POLICY",
        subsections: [
          {
            id: "updates",
            title: "Updates",
            content: [
              "How We Notify You:",
              "• Email notification to registered users",
              "• Prominent notice on our website",
              "• In-app notifications for material changes"
            ]
          },
          {
            id: "continued-use",
            title: "Continued Use",
            content: "Continued use of the Service after changes constitutes acceptance of the updated Privacy Policy."
          },
          {
            id: "version-history",
            title: "Version History",
            content: "We maintain records of significant policy changes and their effective dates."
          }
        ]
      },
  
      {
        id: "enforcement-complaints",
        title: "ENFORCEMENT AND COMPLAINTS",
        subsections: [
          {
            id: "internal-compliance",
            title: "Internal Compliance",
            content: "We regularly review our privacy practices and update procedures to ensure compliance with this policy and applicable laws."
          },
          {
            id: "external-complaints",
            title: "External Complaints",
            content: "Regulatory Authorities: If you believe we have violated your privacy rights, you may file complaints with:",
            list: [
              "State attorneys general",
              "Federal Trade Commission (FTC)",
              "Relevant data protection authorities"
            ]
          }
        ]
      }
    ]
  };
  